import styled, { css } from 'styled-components/macro';

type ButtonStyledT = {
  hasIcon: boolean;
  isLoading: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'contained' | 'outline' | 'outline-rounded' | 'text';
  iconHoverColor?: 'black' | 'white' | 'darkBlue' | 'blue';
};

const contained = css`
  color: ${({ theme }) => theme.colors.white};
  border: ${({ theme }) => theme.borders.xs};
  border-color: ${({ theme }) => theme.colors.primary.variant16};
  background-color: ${({ theme }) => theme.colors.primary.variant16};
  border-radius: ${({ theme }) => theme.radius.sm};

  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }) => theme.colors.secondary.variant5};
      border-color: ${({ theme }) => theme.colors.secondary.variant5};
    }
  }
`;

const outline = css`
  color: ${({ theme }) => theme.colors.primary.variant1};
  border: ${({ theme }) => `${theme.borders.xs} ${theme.colors.rgba.variant5}`};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.radius.sm};

  @media (hover: hover) {
    &:hover {
      color: ${({ theme }) => theme.colors.secondary.variant5};
      border-color: ${({ theme }) => theme.colors.secondary.variant5};
    }
  }
`;

const outlineRounded = css`
  color: ${({ theme }) => theme.colors.rgba.variant4};
  border: ${({ theme }) => `${theme.borders.xs} ${theme.colors.primary.variant6}`};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.radius['2xl']};

  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }) => theme.colors.secondary.variant3};
      border-color: ${({ theme }) => theme.colors.secondary.variant5};
      color: ${({ theme }) => theme.colors.secondary.variant5};
    }
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.secondary.variant3};
    border-color: ${({ theme }) => theme.colors.primary.variant6};
  }
`;

const text = css`
  border: ${({ theme }) => theme.borders.none};
  color: ${({ theme }) => theme.colors.primary.variant1};
  background-color: ${({ theme }) => theme.colors.transparent};

  &:disabled {
    color: ${({ theme }) => theme.colors.primary.variant7};
  }
`;

const xs = css`
  padding: 0 0.75rem;
  height: 28px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.normal};
`;

const sm = css`
  padding: 0 0.875rem;
  height: 28px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.normal};
`;

const md = css`
  padding: 0 0.875rem;
  height: 30px;
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const lg = css`
  padding: 0 0.875rem;
  height: 36px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.normal};
`;

const xl = css`
  padding: 0 1.25rem;
  height: 38px;
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const whiteHover = css`
  fill: ${({ theme }) => theme.colors.white};
`;

const blackHover = css`
  fill: ${({ theme }) => theme.colors.black};
`;

const darkBlueHover = css`
  fill: ${({ theme }) => theme.colors.primary.variant1};
`;

const blueHover = css`
  fill: ${({ theme }) => theme.colors.secondary.variant5};
`;

const iconBtnCss = css<ButtonStyledT>`
  padding-left: 0.625rem;
  padding-right: 1rem;

  @media (hover: hover) {
    &:hover {
      svg path {
        ${({ iconHoverColor }) =>
          iconHoverColor ? hoverColor[iconHoverColor] : hoverColor['blue']};
      }
    }
  }
`;

const loadingCss = css`
  pointer-events: none;
  cursor: not-allowed;
  opacity: 0.7;
`;

const hoverColor = {
  white: whiteHover,
  black: blackHover,
  darkBlue: darkBlueHover,
  blue: blueHover,
};

const buttonVariant = {
  contained,
  outline,
  'outline-rounded': outlineRounded,
  text,
};

const buttonSize = {
  xs,
  sm,
  md,
  lg,
  xl,
};

export const ButtonStyled = styled.button<ButtonStyledT>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: 0.3s;

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  ${({ variant = 'contained' }) => buttonVariant[variant]}
  ${({ size = 'md' }) => buttonSize[size]}
  ${({ hasIcon }) => (hasIcon ? iconBtnCss : '')}
  ${({ isLoading }) => (isLoading ? loadingCss : '')}
`;

export const IconWrapStyled = styled.span`
  display: inline-flex;
  margin-right: 0.625rem;
`;
